import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import SubHeader from '../../components/SubHeader.jsx';
import EmployeeProjectGroupHeader from '../../components/employees/EmployeeProjectGroupHeader.jsx';
import EmployeeTabMessage from '../../components/employees/EmployeeTabMessage.jsx';
import StakeholdersTable, {
  STAKEHOLDER_ROW_ACTIONS,
} from '../../components/projects/Stakeholders/StakeholdersTable.jsx';
import StakeholderEditorFieldModal from '../../components/projects/Stakeholders/StakeholderEditorFieldModal.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import { getEmployeeTabPermissions } from '../../lib/employeeProfilePermissions.js';
import { selectUserPermissions } from '../../modules/auth.js';
import {
  retrieveEmployeeStakeholderProfiles,
  selectEmployee,
  selectEmployeeStakeholderProfilesState,
} from '../../modules/employees.js';
import EmployeeProfileHeaderContainer from './EmployeeProfileHeaderContainer.jsx';

const mapStateToProps = (state, ownProps) => ({
  employeeLoaded: Boolean(selectEmployee(ownProps.employeeId)(state)),
  listState: selectEmployeeStakeholderProfilesState(state),
  permissions: selectUserPermissions(state),
});

const mapDispatchToProps = {
  retrieveEmployeeStakeholderProfiles,
};

// Stakeholder Profiles tab of the employee profile. One table per project,
// because stakeholder columns (names, order, visibility) are configured per project.
const EmployeeStakeholderProfilesContainer = ({
  employeeId,
  search,
  employeeLoaded,
  listState,
  permissions,
  retrieveEmployeeStakeholderProfiles,
}) => {
  const [editorFieldContent, setEditorFieldContent] = useState(null);
  const { canSeeStakeholderProfilesTab } =
    getEmployeeTabPermissions(permissions);

  useEffect(() => {
    // no request the viewer is not allowed to make: a 403 signs the user out
    if (!canSeeStakeholderProfilesTab) return;
    retrieveEmployeeStakeholderProfiles(employeeId, { search }).catch(() => {});
  }, [employeeId, search, canSeeStakeholderProfilesTab]);

  const { projects, total, pending, error } = listState;
  const isCurrentEmployee = listState.employeeId === employeeId;

  const renderProjectGroup = ({ project, stakeholderFields, stakeholders }) => (
    <section key={project.id}>
      <EmployeeProjectGroupHeader project={project} />
      {stakeholders.length ? (
        <StakeholdersTable
          stakeholders={stakeholders}
          project={{ ...project, stakeholderFields }}
          actionsVariant={STAKEHOLDER_ROW_ACTIONS.VIEW_BUTTON}
          onShowCustomEditorField={(title, editorData) =>
            setEditorFieldContent({ title, editorData })
          }
        />
      ) : (
        <EmployeeTabMessage>
          No stakeholder profiles in this {getOrgTermsContent('project')}.
        </EmployeeTabMessage>
      )}
    </section>
  );

  const renderContent = () => {
    if (!canSeeStakeholderProfilesTab) {
      return (
        <EmployeeTabMessage>
          You don’t have access to stakeholder profiles.
        </EmployeeTabMessage>
      );
    }
    if (error && !pending) {
      return (
        <EmployeeTabMessage>
          Stakeholder profiles could not be loaded.
        </EmployeeTabMessage>
      );
    }
    if (!isCurrentEmployee || (pending && !projects.length)) {
      return <EmployeeTabMessage>Loading...</EmployeeTabMessage>;
    }
    if (!projects.length) {
      return (
        <EmployeeTabMessage>
          {search
            ? 'No stakeholder profiles match your search.'
            : 'Employee isn’t part of any stakeholder profiles yet.'}
        </EmployeeTabMessage>
      );
    }
    return (
      <div style={{ opacity: pending ? 0.5 : 1 }}>
        {projects.map(renderProjectGroup)}
      </div>
    );
  };

  return (
    <React.Fragment>
      <EmployeeProfileHeaderContainer employeeId={employeeId} />
      {employeeLoaded ? (
        <section>
          <SubHeader flexJustify small>
            <div className="flex">
              <h2>Stakeholder Profiles</h2>
              <strong className="header-item bordered text-muted">
                Total:&nbsp;
                <span className="text-primary">
                  {canSeeStakeholderProfilesTab && isCurrentEmployee
                    ? total
                    : 0}
                </span>
              </strong>
            </div>
          </SubHeader>
          {renderContent()}
        </section>
      ) : null}
      <StakeholderEditorFieldModal
        show={Boolean(editorFieldContent)}
        onHide={() => setEditorFieldContent(null)}
        resource={editorFieldContent}
      />
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EmployeeStakeholderProfilesContainer);
